// components/SubTabs.js
import React, { useEffect } from "react";

export default function SubTabs({
  activeMainTab,
  activeSubTab,
  setActiveSubTab,
  isProcessed,
  hasDownloaded,
}) {
  // Tabs that should NEVER show subtabs (single-page tabs)
  const NO_SUBTAB_TABS = [11, 12]; // 11 = My Shop / My Storefront, 12 = My Projects

  // Pill radius
  const TAB_RADIUS_PX = 999;

  // 1) One-time keyframes injection (top-level, not conditional)
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      @keyframes tab-flash {
        0% { color: red; }
        25% { color: orange; }
        50% { color: green; }
        75% { color: blue; }
        100% { color: purple; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // 2) Manage subtab defaults based on main tab (top-level, not conditional)
  useEffect(() => {
    // Tabs that have no subtabs → force null
    if (NO_SUBTAB_TABS.includes(activeMainTab)) {
      if (activeSubTab !== null) setActiveSubTab(null);
      return;
    }

    // Tabs 1–7 have subtabs 1 & 2 → default to 1 if null/invalid
    if ([1, 2, 3, 4, 5, 6, 7].includes(activeMainTab)) {
      if (![1, 2].includes(activeSubTab)) setActiveSubTab(1);
      return;
    }

    // All other main tabs (currently disabled) → no subtab
    if (activeSubTab !== null) setActiveSubTab(null);
  }, [activeMainTab, activeSubTab, setActiveSubTab]);

  // 3) If this main tab has no subtabs, render nothing (AFTER hooks)
  if (NO_SUBTAB_TABS.includes(activeMainTab)) return null;

  const subtabs = [
    { id: 1, label: "Process Image" },
    { id: 2, label: "Build 3D Model" },
  ];

  // Enable subtabs for main tabs 1–7
  // SubTab 2 will be gated by `isProcessed`
  const enabledMainTabs = [1, 2, 3, 4, 5, 6, 7];

  return (
    <div
      style={{
        display: "flex",
        backgroundColor: "#f8fafc",
        borderBottom: "1px solid #cbd5e1",
        padding: "4px 8px",
      }}
    >
      {subtabs.map(({ id, label }) => {
        const isActive = id === activeSubTab;
        const isFlashing = id === 2 && isProcessed && !hasDownloaded;

        // SubTab 1 is always enabled for enabled main tabs
        // SubTab 2 only enabled when `isProcessed` is true
        const isEnabledTab =
          enabledMainTabs.includes(activeMainTab) &&
          (id === 1 || (id === 2 && isProcessed));

        return (
          <button
            key={id}
            onClick={() => isEnabledTab && setActiveSubTab(id)}
            disabled={!isEnabledTab}
            style={{
              padding: "6px 14px",
              marginRight: "5px",
              fontSize: "2vh",
              fontWeight: isActive ? "600" : "400",
              backgroundColor: isActive ? "white" : "#f1f5f9",
              border: isActive ? "1px solid #cbd5e1" : "none",
              borderBottom: isActive ? "none" : "1px solid #cbd5e1",

              // Pill tabs (fully rounded)
              borderTopLeftRadius: `${TAB_RADIUS_PX}px`,
              borderTopRightRadius: `${TAB_RADIUS_PX}px`,
              borderBottomLeftRadius: `${TAB_RADIUS_PX}px`,
              borderBottomRightRadius: `${TAB_RADIUS_PX}px`,

              color: isFlashing ? undefined : isActive ? "#000" : "#6b7280",
              animation: isFlashing ? "tab-flash 1.5s infinite" : "none",
              cursor: isEnabledTab ? "pointer" : "not-allowed",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
